import { useState } from "react";

import { useDraggable } from "@dnd-kit/core";
import { isPdfContentType, useAlertContext } from "@shared";

import { DocumentGroup, DocumentResponse } from "@/api";
import { FileActions } from "@/common/components/layout/components/dropzone/DropZone.types";
import { useDocumentDndContext } from "@/common/components/layout/components/dropzone/dnd/DocumentDndContext";
import { DragData } from "@/common/components/layout/components/dropzone/dnd/DropZoneDnd.types";
import { useRenameInput } from "@/common/components/rename-input/hooks/useRenameInput";
import { downloadFile } from "@/common/helper/fileDownloadHelper";
import { useTranslation } from "@/translations";

import { useDocumentPreview } from "./useDocumentPreview";

interface UseDocumentWidgetParams {
  file: DocumentResponse;
  sourceZone: DocumentGroup;
  fileActions: FileActions;
}

export function useDocumentWidget({ file, sourceZone, fileActions }: UseDocumentWidgetParams) {
  const { t } = useTranslation();
  const { showError } = useAlertContext();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [downloadBlob, setDownloadBlob] = useState<{ contentType: string; blob: Blob } | null>(null);

  const preview = useDocumentPreview({
    file,
    fileActions,
    onPreviewOpen: () => setMenuAnchor(null),
  });

  const rename = useRenameInput({
    fileName: file.fileName,
    onRename: (newFileName) => fileActions.onRename(file, newFileName),
  });

  const { isMoving } = useDocumentDndContext();
  const dragData: DragData = { file, sourceZone };
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: `${sourceZone}-${file.documentId}`,
    data: dragData,
    disabled: rename.isEditing || preview.isPreviewOpen || isMoving,
    attributes: { roleDescription: t("dropzone.dnd.draggableRoleDescription") },
  });

  const ensureDownloadBlob = async (): Promise<Blob> => {
    if (downloadBlob?.contentType === file.contentType) {
      return downloadBlob.blob;
    }
    const fetched = await fileActions.onFetchDownloadBlob(file);
    setDownloadBlob({ contentType: file.contentType, blob: fetched });
    return fetched;
  };

  const handleDownload = () => {
    setMenuAnchor(null);
    ensureDownloadBlob()
      .then((b) => downloadFile(b, file.fileName))
      .catch(() => showError(t("dropzone.fileList.downloadError")));
  };

  const handleDeleteClick = () => {
    setMenuAnchor(null);
    fileActions.onDelete(file);
  };

  const handleRenameClick = () => {
    setMenuAnchor(null);
    rename.startEditing();
  };

  const { onCreateCaseNote, onConvertToPdf } = fileActions;
  const handleCreateCaseNoteClick = onCreateCaseNote
    ? () => {
        setMenuAnchor(null);
        onCreateCaseNote(file);
      }
    : undefined;

  const handleConvertToPdfClick =
    onConvertToPdf && !isPdfContentType(file.contentType)
      ? () => {
          setMenuAnchor(null);
          onConvertToPdf(file);
        }
      : undefined;

  return {
    menuAnchor,
    setMenuAnchor,
    rename,
    preview,
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    isDragging,
    handleDownload,
    handleDeleteClick,
    handleRenameClick,
    handleCreateCaseNoteClick,
    handleConvertToPdfClick,
  };
}
